
import moment from 'moment'
import numbro from 'numbro'

import CreateSimulation from '@/components/CreateSimulation.vue'
import CompareSimulations from '@/components/CompareSimulations.vue'
import CreatePlanChooser from '@/components/CreatePlanChooser.vue'
import Simulation from '@/components/Simulation.vue'

import { mapGetters, mapActions } from 'vuex'
import { driver } from '@/mixins/driver.js'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { loadModelReportFromClient } from '@/utils/modelReport.js'
import { effectiveModelStart, clampMonthlyStart, clampWeeklyStart } from '@/utils/modelStart.js'
import { hasHiddenKpis, firstVisibleOutputName } from '@/utils/hiddenKpis.js'
import { calculatorRange, planAxis, buildPrefillLookup, buildFlexibleMediaPlan, flexibleBounds as dataBounds } from '@/utils/flexibleMediaPlan.js'

import _ from 'lodash'

export default {
  mixins: [driver, ngtorchRuns],
  data () {
    return {
      search: '',
      dialog: false,
      chooser: false,
      baseSimulation: null,
      edit: false,
      compare: false,
      selectedSimulation: {},
      expandedSimulations: [],
      isTemplate: false,
      fetchedModelReport: null,
      fetchedModelReportFor: null,
      mismatchDialog: false,
      pendingBase: null,
      pendingEdit: false,
      mismatchInfo: { removed: [], added: [] }
    }
  },
  components: {
    CreateSimulation,
    CompareSimulations,
    CreatePlanChooser,
    NgtorchRunChip,
    Simulation
  },
  methods: {
    ...mapActions('simulations', [
      'loadSimulations'
    ]),
    onCreateOption: function (id, period) {
      var weekly = this.modelFrequency === 'week'
      if (id === 'flexible') {
        this.createFlexibleDates(period)
      } else if (id === 'scratch') {
        this.driver.reset()
        this.dialog = true
      } else if (id === 'actualBudget') {
        if (weekly) this.createLastYearActualBudgetWeekly()
        else this.createLastYearActualBudget()
      } else if (id === 'noBudget') {
        this.createLastYearNoBudget()
      } else if (id === 'onePulse') {
        if (weekly) this.createLastYearOnePulseWeekly()
        else this.createLastYearOnePulseMonthly()
      } else if (id === 'nextYear') {
        if (weekly) this.createNextYearBudgetPropagationWeekly()
        else this.createNextYearBudgetPropagationMonthly()
      }
    },
    // Plan dates of a period picked in the "Flexible Dates" step: months become their first and
    // last day, kept inside the dates with data (flexibleBounds).
    flexibleDates: function (period) {
      var range = this.flexibleBounds
      var from = period.from
      var to = period.to
      if (this.modelFrequency !== 'week') {
        from = from + '-01'
        to = moment(to + '-01').endOf('month').format('YYYY-MM-DD')
      }
      return {
        dateFrom: from < range.start ? range.start : from,
        dateTo: to > range.end ? range.end : to
      }
    },
    flexibleMediaPlan: function (period) {
      var dates = this.flexibleDates(period)
      var axis = planAxis(this.flexibleRange, dates.dateFrom, dates.dateTo, this.modelFrequency)
      return buildFlexibleMediaPlan(axis, Object.keys(this.channels), this.flexibleLookup, this.modelFrequency)
    },
    formatPeriodKey: function (key) {
      return this.modelFrequency === 'week' ? moment(key).format('ll') : moment(key + '-01').format('MMM YYYY')
    },
    flexibleSummary: function (period) {
      var plan = this.flexibleMediaPlan(period)
      var unitKey = this.modelFrequency === 'week' ? 'views.planSimulator.flexibleWeeks' : 'views.planSimulator.flexibleMonths'
      var rows = []
      var sources = [
        { count: plan.counts.actual, icon: 'mdi-history', color: 'primary', label: 'views.planSimulator.flexibleSummaryActual' },
        { count: plan.counts.propagation, icon: 'mdi-calendar-arrow-right', color: 'primary', label: 'views.planSimulator.flexibleSummaryPropagation' },
        { count: plan.counts.empty, icon: 'mdi-circle-outline', color: 'grey', label: 'views.planSimulator.flexibleSummaryEmpty' }
      ]
      sources.forEach(source => {
        if (source.count > 0) {
          rows.push({ icon: source.icon, color: source.color, label: this.$t(source.label), value: this.$tc(unitKey, source.count, { count: source.count }) })
        }
      })
      rows.push({
        icon: 'mdi-cash-multiple',
        color: 'primary',
        label: this.$t('views.planSimulator.flexibleSummaryTotal'),
        value: this.formatValue(plan.total) + ' ' + this.spendUnitName,
        total: true
      })
      return rows
    },
    createFlexibleDates: function (period) {
      if (!period) return
      var dates = this.flexibleDates(period)
      this.baseSimulation = {
        name: this.$t('views.planSimulator.createFlexibleDates'),
        date_from: dates.dateFrom,
        date_to: dates.dateTo,
        media_plan: this.flexibleMediaPlan(period).mediaPlan,
        media_plan_frequency: this.modelFrequency,
        allow_frequency_toggle: false
      }
      this.isTemplate = true
      this.dialog = true
    },
    weeklyLastYearWindow: function (fallbackEndDate, fallbackStartDate) {
      // Sunday-aligned 52-week window (dateTo / dateFrom / length) for the
      // weekly "Last Year" templates. Anchors both the end date and the start
      // floor on the weekly spend series (media_spend_weekly) so the Calculator
      // matches the Plan Optimizer's weekly template exactly. Falls back to the
      // supplied monthly anchor dates when weekly spend isn't available (e.g.
      // the zero-budget "No Budget" template on a model without weekly spend).
      var weekly = this.mediaSpendWeeklyData
      var weeklyDates = weekly ? weekly[Object.keys(weekly)[0]].x : null
      var hasWeekly = weeklyDates && weeklyDates.length > 0
      var endSource = hasWeekly ? weeklyDates[weeklyDates.length - 1] : fallbackEndDate
      var startSource = hasWeekly ? weeklyDates[0] : fallbackStartDate

      // effectiveModelStart keeps the intentional legacy-weekly push-forward; it
      // is a no-op for modern models, so those align with the Optimizer.
      var effStart = effectiveModelStart(startSource, this.modelFrequency, this.currentClientObject.date_created.toDate())

      // Align dateTo to the last Sunday at or before the anchor end date.
      var dateToMoment = moment(endSource)
      if (dateToMoment.day() !== 0) {
        dateToMoment.subtract(dateToMoment.day(), 'days')
      }
      var dateTo = dateToMoment.format('YYYY-MM-DD')
      var dateFrom = clampWeeklyStart(dateToMoment.clone().subtract(51, 'weeks').format('YYYY-MM-DD'), effStart)
      return {
        dateTo: dateTo,
        dateFrom: dateFrom,
        length: moment(dateTo).diff(moment(dateFrom), 'weeks') + 1
      }
    },
    buildLastYearBase: function (options) {
      var granularity = options.granularity
      var zeroBudget = options.zeroBudget
      var spendSource = options.spendSource
      var channelNames = Object.keys(this.channels)

      // We always need media_spend (monthly) at minimum to derive the end date
      if (!this.mediaSpendData) return null

      // Anchor end date to the model's last spend date
      var anchorChannel = Object.keys(this.mediaSpendData)[0]
      var anchorDates = this.mediaSpendData[anchorChannel].x
      var modelEndDate = anchorDates[anchorDates.length - 1]

      var dateFrom, dateTo, expectedLength
      if (granularity === 'week') {
        var weeklyWindow = this.weeklyLastYearWindow(modelEndDate, anchorDates[0])
        dateTo = weeklyWindow.dateTo
        dateFrom = weeklyWindow.dateFrom
        expectedLength = weeklyWindow.length
      } else {
        // Effective model start: clamps the window so models with less than a
        // full year of data (or legacy weekly models) don't begin before real
        // data.
        var effStart = effectiveModelStart(anchorDates[0], this.modelFrequency, this.currentClientObject.date_created.toDate())
        dateTo = modelEndDate
        dateFrom = clampMonthlyStart(moment(modelEndDate).subtract(11, 'months').startOf('month').format('YYYY-MM-DD'), effStart)
        expectedLength = moment(dateTo).startOf('month').diff(moment(dateFrom).startOf('month'), 'months') + 1
      }

      var mediaPlan = {}
      if (zeroBudget || !spendSource) {
        channelNames.forEach(function (channel) {
          mediaPlan[channel] = new Array(expectedLength).fill(0)
        })
      } else {
        var fromSliced = dateFrom.slice(0, 7)
        var toSliced = dateTo.slice(0, 7)
        channelNames.forEach(function (channel) {
          if (spendSource[channel]) {
            var spendDates = spendSource[channel].x
            var spendValues = spendSource[channel].y
            var values = []
            for (var i = 0; i < spendDates.length; i++) {
              var inRange
              if (granularity === 'week') {
                inRange = spendDates[i] >= dateFrom && spendDates[i] <= dateTo
              } else {
                var monthKey = spendDates[i].slice(0, 7)
                inRange = monthKey >= fromSliced && monthKey <= toSliced
              }
              if (inRange) {
                values.push(Math.round(spendValues[i]))
              }
            }
            mediaPlan[channel] = values
          } else {
            mediaPlan[channel] = new Array(expectedLength).fill(0)
          }
        })
        // Pad short arrays so every channel matches expectedLength
        channelNames.forEach(function (ch) {
          if (mediaPlan[ch].length < expectedLength) {
            mediaPlan[ch] = mediaPlan[ch].concat(new Array(expectedLength - mediaPlan[ch].length).fill(0))
          }
        })
      }

      return {
        name: this.$t(options.i18nNameKey),
        date_from: dateFrom,
        date_to: dateTo,
        media_plan: mediaPlan,
        media_plan_frequency: granularity,
        allow_frequency_toggle: !!options.allowFrequencyToggle
      }
    },
    createLastYearActualBudget: function () {
      var base = this.buildLastYearBase({
        granularity: 'month',
        zeroBudget: false,
        spendSource: this.mediaSpendData,
        i18nNameKey: 'views.planSimulator.createLastYearActualBudget',
        allowFrequencyToggle: false
      })
      if (!base) return
      this.baseSimulation = base
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearActualBudgetWeekly: function () {
      var base = this.buildLastYearBase({
        granularity: 'week',
        zeroBudget: false,
        spendSource: this.mediaSpendWeeklyData,
        i18nNameKey: 'views.planSimulator.createLastYearActualBudgetWeekly',
        allowFrequencyToggle: false
      })
      if (!base) return
      this.baseSimulation = base
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearNoBudget: function () {
      var base = this.buildLastYearBase({
        granularity: this.modelFrequency,
        zeroBudget: true,
        i18nNameKey: 'views.planSimulator.createLastYearNoBudget',
        allowFrequencyToggle: true
      })
      if (!base) return
      this.baseSimulation = base
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearOnePulseMonthly: function () {
      if (!this.hasMediaSpendData || !this.hasSaturationData) return

      var mediaSpend = this.mediaSpendData
      var saturation = this.parsedModelReport.saturation
      var saturationGranularity = this.referenceModelConfig
        ? this.referenceModelConfig.saturation_granularity
        : null

      var monthlyMultiplier = saturationGranularity === 'W'
        ? (365.25 / 12 / 7)
        : 1

      var firstChannel = Object.keys(mediaSpend)[0]
      var allDatesFromSpend = mediaSpend[firstChannel].x
      var dateTo = allDatesFromSpend[allDatesFromSpend.length - 1]
      var effStart = effectiveModelStart(allDatesFromSpend[0], this.modelFrequency, this.currentClientObject.date_created.toDate())
      var dateFrom = clampMonthlyStart(moment(dateTo).subtract(11, 'months').startOf('month').format('YYYY-MM-DD'), effStart)
      var monthsCount = moment(dateTo).startOf('month').diff(moment(dateFrom).startOf('month'), 'months') + 1

      var channelNames = Object.keys(this.channels)
      var mediaPlan = {}
      channelNames.forEach(function (channel) {
        var saturationValue = 0
        if (saturation[channel] &&
            Array.isArray(saturation[channel].regions) &&
            saturation[channel].regions.length > 0) {
          var regions = saturation[channel].regions
          saturationValue = regions[regions.length - 1] * monthlyMultiplier
        }
        var values = new Array(monthsCount).fill(0)
        values[0] = Math.round(saturationValue)
        mediaPlan[channel] = values
      })

      this.baseSimulation = {
        name: this.$t('views.planSimulator.createLastYearOnePulseMonthly'),
        date_from: dateFrom,
        date_to: dateTo,
        media_plan: mediaPlan,
        media_plan_frequency: 'month',
        allow_frequency_toggle: false
      }
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearOnePulseWeekly: function () {
      if (!this.hasMediaSpendData || !this.hasSaturationData) return

      var mediaSpend = this.mediaSpendData
      var saturation = this.parsedModelReport.saturation
      var saturationGranularity = this.referenceModelConfig
        ? this.referenceModelConfig.saturation_granularity
        : null

      var weeklyMultiplier = saturationGranularity === 'M'
        ? (7 * 12 / 365.25)
        : 1

      var firstChannel = Object.keys(mediaSpend)[0]
      var allDatesFromSpend = mediaSpend[firstChannel].x
      // Same Sunday-aligned weekly window as the weekly Actual Budget template,
      // so both weekly templates start on the same date. Aligning dateTo to the
      // last Sunday also keeps CreateSimulation's stopDate (which jumps forward
      // to the next Sunday) from generating an extra week the array can't fill.
      var weeklyWindow = this.weeklyLastYearWindow(allDatesFromSpend[allDatesFromSpend.length - 1], allDatesFromSpend[0])
      var dateTo = weeklyWindow.dateTo
      var dateFrom = weeklyWindow.dateFrom
      var weeksCount = weeklyWindow.length

      var channelNames = Object.keys(this.channels)
      var mediaPlan = {}
      channelNames.forEach(function (channel) {
        var saturationValue = 0
        if (saturation[channel] &&
            Array.isArray(saturation[channel].regions) &&
            saturation[channel].regions.length > 0) {
          var regions = saturation[channel].regions
          saturationValue = regions[regions.length - 1] * weeklyMultiplier
        }
        var values = new Array(weeksCount).fill(0)
        values[0] = Math.round(saturationValue)
        mediaPlan[channel] = values
      })

      this.baseSimulation = {
        name: this.$t('views.planSimulator.createLastYearOnePulseWeekly'),
        date_from: dateFrom,
        date_to: dateTo,
        media_plan: mediaPlan,
        media_plan_frequency: 'week',
        allow_frequency_toggle: false
      }
      this.isTemplate = true
      this.dialog = true
    },
    buildNextYearBase: function (options) {
      var propagation = this.budgetPropagationData
      if (!propagation) return null

      var granularity = options.granularity
      var channelPropagation = propagation[granularity]
      var propagationDates = propagation.dates ? propagation.dates[granularity] : null
      if (!channelPropagation || !propagationDates || propagationDates.length === 0) return null

      var channelNames = Object.keys(this.channels)
      var expectedLength = propagationDates.length

      var mediaPlan = {}
      channelNames.forEach(function (channel) {
        if (Array.isArray(channelPropagation[channel])) {
          mediaPlan[channel] = channelPropagation[channel].map(function (v) { return Math.round(v) })
        } else {
          mediaPlan[channel] = new Array(expectedLength).fill(0)
        }
      })

      return {
        name: this.$t(options.i18nNameKey),
        date_from: propagationDates[0],
        date_to: propagationDates[propagationDates.length - 1],
        media_plan: mediaPlan,
        media_plan_frequency: granularity === 'weekly' ? 'week' : 'month',
        allow_frequency_toggle: false
      }
    },
    createNextYearBudgetPropagationMonthly: function () {
      var base = this.buildNextYearBase({
        granularity: 'monthly',
        i18nNameKey: 'views.planSimulator.createNextYearBudgetPropagationMonthly'
      })
      if (!base) return
      this.baseSimulation = base
      this.isTemplate = true
      this.dialog = true
    },
    createNextYearBudgetPropagationWeekly: function () {
      var base = this.buildNextYearBase({
        granularity: 'weekly',
        i18nNameKey: 'views.planSimulator.createNextYearBudgetPropagationWeekly'
      })
      if (!base) return
      this.baseSimulation = base
      this.isTemplate = true
      this.dialog = true
    },
    channelMismatch: function (base) {
      if (!base || !base.media_plan) return null
      var planChannels = Object.keys(base.media_plan)
      var modelChannels = Object.keys(this.channels)
      var removed = planChannels.filter(function (c) { return modelChannels.indexOf(c) === -1 })
      var added = modelChannels.filter(function (c) { return planChannels.indexOf(c) === -1 })
      if (removed.length === 0 && added.length === 0) return null
      return { removed: removed, added: added }
    },
    channelLabel: function (key) {
      if (this.currentClientObject && this.currentClientObject.pretty_names &&
          this.currentClientObject.pretty_names.media && this.currentClientObject.pretty_names.media[key]) {
        return this.currentClientObject.pretty_names.media[key]
      }
      if (this.channels[key] && this.channels[key].name) {
        return this.channels[key].name
      }
      return key
    },
    openMismatchWarning: function (base, isEdit, mismatch) {
      this.pendingBase = base
      this.pendingEdit = isEdit
      this.mismatchInfo = mismatch
      this.mismatchDialog = true
    },
    proceedWithMismatch: function () {
      this.mismatchDialog = false
      this.baseSimulation = this.pendingBase
      this.edit = this.pendingEdit
      this.dialog = true
      this.pendingBase = null
    },
    cloneSimulation: function (e) {
      var mismatch = this.channelMismatch(e.base)
      if (mismatch) {
        this.openMismatchWarning(e.base, false, mismatch)
        return
      }
      this.baseSimulation = e.base
      this.dialog = true
    },
    editSimulation: function (e) {
      var mismatch = this.channelMismatch(e.base)
      if (mismatch) {
        this.openMismatchWarning(e.base, true, mismatch)
        return
      }
      this.baseSimulation = e.base
      this.edit = true
      this.dialog = true
    },
    compareSimulation: function (e) {
      this.selectedSimulation = e.simulation
      this.compare = true
    },
    highlightCreateSimulation: function () {
      this.driver.highlight('#create-simulation')
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatDateFromNow (date) {
      return moment(date).fromNow()
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode) {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    },
    predictedSalesValue (item) {
      if (!item.results || !item.results.sales) return null
      const sales = item.results.sales
      let salesObj, outputName
      // The Total adds up every output, so it is not shown while any KPI is hidden: the column
      // then shows the first visible KPI instead (see listOutputName).
      const hideTotal = !!sales.outputs && hasHiddenKpis(this.currentClientObject)
      if (sales.total && !hideTotal) {
        salesObj = sales
        outputName = null
      } else if (sales.outputs) {
        outputName = firstVisibleOutputName(this.currentClientObject, sales.outputs)
        if (!outputName) return null
        salesObj = sales.outputs[outputName]
      } else {
        return null
      }
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const isOutputAdditive = (name) => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      }
      let mode
      if (outputName) {
        mode = isOutputAdditive(outputName) ? 'sum' : 'average'
      } else {
        const realOutputs = sales.outputs ? Object.keys(sales.outputs) : []
        mode = realOutputs.every(name => isOutputAdditive(name)) ? 'sum' : 'average'
      }
      if (salesObj && salesObj.sales_decomposition_by_period) {
        return this.aggregateSalesDecompositionByPeriod(salesObj.sales_decomposition_by_period, mode).total
      }
      if (salesObj && salesObj.total) {
        return salesObj.total
      }
      return null
    },
    reportableSales: function (item) {
      const v = this.predictedSalesValue(item)
      return v == null ? null : this.formatValue(v)
    },
    customSort: function (items, sortBy, sortDesc) {
      const sortCol = sortBy[0]
      if (['date_created', 'date_from'].includes(sortCol)) {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return new Date(a[sortCol]) - new Date(b[sortCol])
          })
        } else {
          items.sort((a, b) => {
            return new Date(b[sortCol]) - new Date(a[sortCol])
          })
        }
      } else if (sortCol === 'results.sales.total') {
        const dir = sortDesc[0] ? -1 : 1
        items.sort((a, b) => {
          const av = this.predictedSalesValue(a)
          const bv = this.predictedSalesValue(b)
          if (av == null && bv == null) return 0
          if (av == null) return 1
          if (bv == null) return -1
          return (av - bv) * dir
        })
      } else {
        console.log(sortCol)
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return _.get(b, sortCol, '').toString().localeCompare(_.get(a, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return _.get(a, sortCol, '').toString().localeCompare(_.get(b, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    },
    rowClick: function (item) {
      if (this.expandedSimulations.includes(item)) {
        const index = this.expandedSimulations.indexOf(item)
        this.$delete(this.expandedSimulations, index)
      } else {
        this.expandedSimulations.push(item)
      }
    }
  },
  computed: {
    ...mapGetters('clients', [
      'clients',
      'currentClient',
      'currentClientObject'
    ]),
    ...mapGetters('auth', [
      'authUser',
      'isExternalUser'
    ]),
    ...mapGetters('simulations', [
      'simulations',
      'simulationsLoading'
    ]),
    referenceModelConfig () {
      if (this.currentClientObject && this.currentClientObject.plan_reference_model) {
        return this.currentClientObject.plan_reference_model.config
      } else if (this.currentClientObject && this.currentClientObject.reference_model) {
        return this.currentClientObject.reference_model.config
      }
      return null
    },
    modelFrequency () {
      if (!this.referenceModelConfig) return 'month'
      return this.referenceModelConfig.saturation_granularity === 'W' ? 'week' : 'month'
    },
    parsedModelReport () {
      if (!this.currentClientObject) return null
      if (this.currentClientObject.model_report_storage) {
        return this.fetchedModelReport
      }
      if (!this.currentClientObject.model_report) return null
      var modelReport = this.currentClientObject.model_report
      if (typeof modelReport === 'string') {
        try {
          return JSON.parse(modelReport)
        } catch (e) {
          console.error('Failed to parse model_report:', e)
          return null
        }
      }
      return modelReport
    },
    modelReportFetchKey () {
      if (!this.currentClientObject || !this.currentClientObject.model_report_storage) return null
      const ts = this.currentClientObject.model_report_updated_at
      let tsKey = ''
      if (ts) {
        if (typeof ts.toMillis === 'function') tsKey = String(ts.toMillis())
        else if (ts.seconds !== undefined) tsKey = ts.seconds + '.' + (ts.nanoseconds || 0)
        else tsKey = String(ts)
      }
      return this.currentClientObject.model_report_storage + '@' + tsKey
    },
    // Dates the Calculator form allows, for the "Flexible Dates" period picker.
    flexibleRange () {
      if (!this.currentClientObject || !this.referenceModelConfig) return null
      return calculatorRange(this.currentClientObject)
    },
    // Actual spend and budget propagation per period, at the model's frequency.
    flexibleLookup () {
      return buildPrefillLookup(this.parsedModelReport, this.modelFrequency)
    },
    // The part of flexibleRange the report has data for: the dates the "Flexible Dates" calendars allow.
    flexibleBounds () {
      return dataBounds(this.flexibleRange, this.flexibleLookup, this.modelFrequency)
    },
    // Period the "Flexible Dates" calendar opens on, in its format (YYYY-MM or YYYY-MM-DD): the
    // budget propagation year when there is one, else the last year of actual spend. Null when
    // the report has neither, which hides the template.
    flexibleDefaultPeriod () {
      if (!this.flexibleBounds) return null
      var weekly = this.modelFrequency === 'week'
      var from = null
      var to = null
      var propagation = this.budgetPropagationData
      var hasPropagation = weekly ? this.hasBudgetPropagationWeeklyData : this.hasBudgetPropagationMonthlyData
      if (hasPropagation) {
        var propagationDates = propagation.dates[weekly ? 'weekly' : 'monthly']
        from = propagationDates[0]
        to = propagationDates[propagationDates.length - 1]
      } else if (weekly && this.hasMediaSpendWeeklyData) {
        var weeklyWindow = this.weeklyLastYearWindow(null, null)
        from = weeklyWindow.dateFrom
        to = weeklyWindow.dateTo
      } else if (!weekly && this.hasMediaSpendData) {
        var spendDates = this.mediaSpendData[Object.keys(this.mediaSpendData)[0]].x
        to = spendDates[spendDates.length - 1]
        from = moment(to).subtract(11, 'months').startOf('month').format('YYYY-MM-DD')
      } else {
        return null
      }
      var range = this.flexibleBounds
      from = from < range.start ? range.start : (from > range.end ? range.end : from)
      to = to > range.end ? range.end : (to < from ? from : to)
      return weekly ? [from, to] : [from.slice(0, 7), to.slice(0, 7)]
    },
    // Where the pre-filled values come from, under the "Flexible Dates" summary.
    flexibleHint () {
      var parts = []
      var actualKeys = Object.keys(this.flexibleLookup.actual).sort()
      var propagationKeys = Object.keys(this.flexibleLookup.propagation).sort()
      if (actualKeys.length > 0) {
        parts.push(this.$t('views.planSimulator.flexibleHintActual', { date: this.formatPeriodKey(actualKeys[actualKeys.length - 1]) }))
      }
      if (propagationKeys.length > 0) {
        parts.push(this.$t('views.planSimulator.flexibleHintPropagation', {
          from: this.formatPeriodKey(propagationKeys[0]),
          to: this.formatPeriodKey(propagationKeys[propagationKeys.length - 1])
        }))
      }
      return parts.join(' ')
    },
    // True while the Storage-hosted model report the templates are built from is being fetched.
    modelReportLoading () {
      return !!this.modelReportFetchKey && this.fetchedModelReportFor !== this.modelReportFetchKey
    },
    // Cards of the "Create Plan" modal, with the same availability rules the old dropdown had.
    // Monthly and weekly variants share a card: the model's frequency picks one in onCreateOption.
    createOptions () {
      var weekly = this.modelFrequency === 'week'
      var options = [{
        id: 'scratch',
        featured: true,
        icon: 'mdi-file-document-edit-outline',
        title: this.$t('views.planSimulator.createFromScratch'),
        description: this.$t('views.planSimulator.chooseFromScratchDescription')
      }]
      if (this.modelReportLoading) return options
      if (this.flexibleDefaultPeriod) {
        options.push({
          id: 'flexible',
          icon: 'mdi-calendar-range',
          title: this.$t('views.planSimulator.chooseFlexibleTitle'),
          description: this.$t('views.planSimulator.chooseFlexibleDescription'),
          tag: this.$t('views.planSimulator.chooseTagFlexible'),
          period: {
            type: weekly ? 'date' : 'month',
            min: weekly ? this.flexibleBounds.start : this.flexibleBounds.start.slice(0, 7),
            max: weekly ? this.flexibleBounds.end : this.flexibleBounds.end.slice(0, 7),
            // No period comes selected: the calendar only opens where the pre-filled data starts.
            view: this.flexibleDefaultPeriod[0],
            hint: this.flexibleHint,
            summary: this.flexibleSummary
          }
        })
      }
      if (weekly ? this.hasMediaSpendWeeklyData : this.hasMediaSpendData) {
        options.push({
          id: 'actualBudget',
          icon: 'mdi-history',
          title: this.$t('views.planSimulator.chooseActualBudgetTitle'),
          description: this.$t('views.planSimulator.chooseActualBudgetDescription'),
          tag: this.$t('views.planSimulator.chooseTagStrategyCheck')
        })
      }
      if (this.hasMediaSpendData) {
        options.push({
          id: 'noBudget',
          icon: 'mdi-table-large',
          title: this.$t('views.planSimulator.chooseNoBudgetTitle'),
          description: this.$t('views.planSimulator.chooseNoBudgetDescription'),
          tag: this.$t('views.planSimulator.chooseTagBlank')
        })
      }
      if (this.hasMediaSpendData && this.hasSaturationData) {
        options.push({
          id: 'onePulse',
          icon: 'mdi-pulse',
          title: this.$t('views.planSimulator.chooseOnePulseTitle'),
          description: this.$t('views.planSimulator.chooseOnePulseDescription'),
          tag: this.$t('views.planSimulator.chooseTagCarryOver')
        })
      }
      if (weekly ? this.hasBudgetPropagationWeeklyData : this.hasBudgetPropagationMonthlyData) {
        options.push({
          id: 'nextYear',
          icon: 'mdi-calendar-arrow-right',
          title: this.$t('views.planSimulator.chooseNextYearTitle'),
          description: this.$t('views.planSimulator.chooseNextYearDescription'),
          tag: this.$t('views.planSimulator.chooseTagForwardLooking')
        })
      }
      return options
    },
    mediaSpendData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.media_spend) {
        return null
      }
      return this.parsedModelReport.v2.media_spend
    },
    hasMediaSpendData () {
      return this.mediaSpendData !== null && Object.keys(this.mediaSpendData).length > 0
    },
    hasSaturationData () {
      if (!this.parsedModelReport || !this.parsedModelReport.saturation) return false
      var sat = this.parsedModelReport.saturation
      var keys = Object.keys(sat)
      if (keys.length === 0) return false
      return keys.some(function (ch) {
        return sat[ch] && Array.isArray(sat[ch].regions) && sat[ch].regions.length > 0
      })
    },
    mediaSpendWeeklyData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.media_spend_weekly) {
        return null
      }
      return this.parsedModelReport.v2.media_spend_weekly
    },
    hasMediaSpendWeeklyData () {
      return this.mediaSpendWeeklyData !== null && Object.keys(this.mediaSpendWeeklyData).length > 0
    },
    budgetPropagationData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.budget_propagation) {
        return null
      }
      return this.parsedModelReport.v2.budget_propagation
    },
    hasBudgetPropagationMonthlyData () {
      var p = this.budgetPropagationData
      return !!(p && p.monthly && Object.keys(p.monthly).length > 0 &&
                p.dates && Array.isArray(p.dates.monthly) && p.dates.monthly.length > 0)
    },
    hasBudgetPropagationWeeklyData () {
      var p = this.budgetPropagationData
      return !!(p && p.weekly && Object.keys(p.weekly).length > 0 &&
                p.dates && Array.isArray(p.dates.weekly) && p.dates.weekly.length > 0)
    },
    customKpiName () {
      if (this.currentClientObject && this.currentClientObject.customKpiName) {
        return this.currentClientObject.customKpiName
      } else {
        return this.$t('views.planSimulator.defaultKpiName')
      }
    },
    ngtorchRunsPlans () {
      return this.filteredSimulations.map(simulation => ({ ...simulation, clientId: this.currentClient }))
    },
    filteredSimulations () {
      let simulations = []
      for (let index = 0; index < this.simulations.length; index++) {
        // Hidden auto-generated Strategy Check plans live only in the What-If Scenario view.
        if (this.simulations[index].strategy_check) {
          continue
        }
        // Budget Simulator grid points show only inside their Budget Simulator.
        if (this.simulations[index].budget_simulation_id) {
          continue
        }
        if ('type' in this.simulations[index] && this.simulations[index].type === 'plan_simulation') {
          // Support stringified results, for supporting very large results objects (Firebase chokes on them and deletes the full simulation)
          if (this.simulations[index].results && typeof this.simulations[index].results === 'string') {
            let sim = { ...this.simulations[index] }
            sim.results = JSON.parse(sim.results)
            simulations.push(sim)
          } else {
            simulations.push({ ...this.simulations[index] })
          }
        }
      }
      if (this.isExternalUser && this.authUser && this.authUser.uid) {
        var uid = this.authUser.uid
        simulations = simulations.filter(function (sim) { return sim.created_by_id === uid || sim.showToExternalUsers })
      }
      return _.sortBy(simulations, 'date_created').reverse()
    },
    parsedSimulations () {
      return _.sortBy(this.simulations.filter(simulation => !simulation.strategy_check && !simulation.budget_simulation_id && simulation.type !== 'budget_simulation').map(simulation => {
        // Support stringified results, for supporting very large results objects (Firebase chokes on them and deletes the full simulation)
        if (simulation.results && typeof simulation.results === 'string') {
          // Parse into a copy: assigning back onto the store object would make this computed drop the plan on its next evaluation
          return { ...simulation, results: JSON.parse(simulation.results) }
        }
        return simulation
      }), 'date_created').reverse()
    },
    channels () {
      if (this.currentClientObject && 'channels' in this.currentClientObject && this.currentClientObject.channels) {
        return this.currentClientObject.channels
      } else if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && this.currentClientObject.reference_model.config.media) {
        var channels = {}
        Object.keys(this.currentClientObject.reference_model.config.media).forEach(key => {
          channels[key] = {
            name: key
          }
        })
        return channels
      } else {
        return {}
      }
    },
    spendUnitName: function () {
      if (this.currentClientObject && this.currentClientObject.plan_reference_model && this.currentClientObject.plan_reference_model.config && 'invest_unit_name' in this.currentClientObject.plan_reference_model.config) {
        return this.currentClientObject.plan_reference_model.config.invest_unit_name
      } else if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && 'invest_unit_name' in this.currentClientObject.reference_model.config) {
        return this.currentClientObject.reference_model.config.invest_unit_name
      } else {
        return this.$t('views.planSimulator.defaultCurrency')
      }
    },
    // The KPI the "Predicted" column shows while some KPI is hidden (the first visible output
    // of the config), or null when nothing is hidden and the column shows the Total.
    listOutputName: function () {
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      if (!configOutputs || !hasHiddenKpis(this.currentClientObject)) return null
      return firstVisibleOutputName(this.currentClientObject, configOutputs)
    },
    currentOutputUnitName: function () {
      if (this.listOutputName) {
        return this.referenceModelConfig.outputs[this.listOutputName].sales_unit_name || this.$t('views.planSimulator.defaultCurrency')
      }
      if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && 'sales_unit_name' in this.currentClientObject.reference_model.config) {
        return this.currentClientObject.reference_model.config.sales_unit_name
      } else {
        return this.$t('views.planSimulator.defaultCurrency')
      }
    },
    simulationHeaders: function () {
      return [
        {
          text: this.$t('views.planSimulator.headerName'),
          align: 'start',
          sortable: true,
          value: 'name',
          width: '350px'
        },
        {
          text: `${this.$t('views.planSimulator.headerBudget')} (${this.spendUnitName})`,
          value: 'budget'
        },
        {
          text: `${this.$t('views.planSimulator.headerPredictedKpi', { kpiName: this.customKpiName })} (${this.currentOutputUnitName})`,
          value: 'results.sales.total'
        },
        {
          text: this.$t('views.planSimulator.headerPeriod'),
          value: 'date_from',
          width: '220px'
        },
        {
          text: this.$t('views.planSimulator.headerCreated'),
          value: 'date_created',
          width: '135px'
        }
      ]
    }
  },
  watch: {
    'currentClient': {
      immediate: true,
      handler () {
        this.loadSimulations({
          client: this.currentClient
        })
      }
    },
    modelReportFetchKey: {
      immediate: true,
      handler (newKey) {
        if (!newKey) {
          this.fetchedModelReport = null
          this.fetchedModelReportFor = null
          return
        }
        if (newKey === this.fetchedModelReportFor) return
        const target = newKey
        loadModelReportFromClient(this.currentClientObject)
          .then(report => {
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = report
              this.fetchedModelReportFor = target
            }
          })
          .catch(err => {
            console.error('Failed to load model_report from Storage:', err)
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = null
              this.fetchedModelReportFor = target
            }
          })
      }
    }
  }
}
